import api from "./api";
import { BACKEND_CONFIG, buildUrl, buildQueryString } from "../config/backend";

export interface Case {
  id: number;
  user_id: string;
  lawyers_assigned: string | string[];
  lawyers_list?: any[];
  supervisor: any;
  customer_id: number;
  type: string;
  subject: string;
  start_date: string;
  end_date: string;
  billing_type: string;
  fee: number;
  supervisor_fee: number;
  fixed_fee?: number;
  total_charges: number;
  currency: string;
  files: any[];
  timer: number;
  current_state: string;
  status: string;
  priority?: string;
  reviewed: boolean;
  created_at: string;
  updated_at: string;
  enable_public_access?: boolean;
  public_access_code?: string;
  // Only present when the backend eager-loads it (e.g. the public case
  // details endpoint). `items` on each bill is a JSON-encoded string, not
  // a parsed array — the backend stores it via `json_encode()`.
  bills?: Array<{
    id: number;
    bill_number: string;
    items: string | Array<{ description: string; quantity: number; unit_price: number; total: number }>;
    subtotal: number;
    total: number;
    status: string;
    currency: string;
    issued_date?: string;
  }>;
}

export interface CaseCreateInput {
  user_id?: string;
  lawyers_assigned: string | string[];
  supervisor: any;
  customer_id: number;
  type: string;
  subject: string;
  start_date: string;
  end_date?: string;
  billing_type: string;
  fee: number;
  supervisor_fee?: number;
  fixed_fee?: number;
  total_charges?: number;
  currency: string;
  status?: string;
  priority?: string;
  enable_public_access?: boolean;
  public_access_code?: string;
}

export interface CaseUpdateInput extends Partial<CaseCreateInput> {
  id: number;
}

const casesService = {
  getCases: async (
    userId: string,
    page = 1,
    limit = 20,
    filters: Record<string, any> = {},
  ) => {
    const params: Record<string, any> = { page, per_page: limit, ...filters };
    const response = await api.get(
      `/cases/list/${userId}?${buildQueryString(params)}`,
    );
    return response.data;
  },

  getCaseById: async (orderId: string, userId: string) => {
    const response = await api.get(`/cases/${orderId}/${userId}`);
    return response.data;
  },

  createCase: async (caseData: any) => {
    const response = await api.post("/cases", caseData);
    return response.data;
  },

  updateCase: async (caseData: any) => {
    const response = await api.put("/cases", caseData);
    return response.data;
  },

  deleteCase: async (orderId: string, userId: string) => {
    const response = await api.delete(`/cases/${orderId}/${userId}`);
    return response.data;
  },

  updateCaseStatus: async (caseData: any) => {
    const response = await api.put("/cases/status", caseData);
    return response.data;
  },

  updateCaseSteps: async (caseData: any) => {
    const response = await api.put("/cases/steps", caseData);
    return response.data;
  },

  searchCases: async (
    userId: string,
    query: string,
    filters: Record<string, any> = {},
  ) => {
    const params: Record<string, any> = { q: query, ...filters };
    const response = await api.get(
      `/cases/search/${userId}?${buildQueryString(params)}`,
    );
    return response.data;
  },

  uploadCaseFiles: async (caseId: number, files: File[]) => {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append("files[]", file);
    });

    const response = await api.post(`/cases/${caseId}/files`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  },

  // "Event" is the harmonized product term for what the backend still
  // calls a case-steps-tracker / "activity log" entry — these methods
  // keep the (non-user-facing) /cases/activity-logs API paths unchanged
  // but are named to match the UI terminology.
  addCaseEvent: async (timeData: {
    case_id: number;
    title: string;
    lawyer_id?: string;
    time_spent: number;
    description: string;
    status?: string;
  }) => {
    const params = { ...timeData, created_at: new Date().toISOString() };
    const response = await api.post("/cases/activity-logs", params);
    return response.data;
  },

  getCaseEvents: async (case_id: number) => {
    const response = await api.get(`/cases/activity-logs/case/${case_id}`);
    return response.data;
  },

  updateCaseEvent: async (logData: {
    id: number;
    case_id: number;
    title: string;
    lawyer_id?: string | null;
    time_spent?: number;
    status?: string;
    description: string;
  }) => {
    const response = await api.put("/cases/activity-logs", logData);
    return response.data;
  },

  deleteCaseEvent: async (id: number) => {
    const response = await api.delete("/cases/activity-logs", { data: { id } });
    return response.data;
  },

  getPublicCaseDetails: async (caseId: number, firmId: number, accessCode: string) => {
    const response = await api.get(`/cases/public/${caseId}/${firmId}/${accessCode}`);
    return response.data;
  },

  getPublicCaseEvents: async (caseId: number, accessCode: string) => {
    const response = await api.get(`/cases/public/activity-logs/${caseId}/${accessCode}`);
    return response.data;
  },

  updateCaseTotalCharges: async (caseId: number, totalCharges: number) => {
    const response = await api.put(`/cases/${caseId}/total-charges`, { total_charges: totalCharges });
    return response.data;
  },
};

export default casesService;
